import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import EsaedroHistoryItem from "../../../../Models/EsaedroHistoryItem";
import EsaedroResourceFilter from "../../../../Models/EsaedroResourceFilter";
import { ResourceViewField } from "../../../../Models/SysConf/ResourceViewField";
import {
  createObjectUniqueName,
  ObjectNames,
} from "../../../../Utils/ObjectNames";
import { add } from "lodash";
import { MenuItemGridCustomAction } from "../../../../Models/SysConf/MenuItemGridCustomAction";
import EsaedroSortField from "../../../../Models/EsaedroSortField";

export interface TabbedPageObjectList {
  id: number;

  filters: EsaedroResourceFilter[];
  sorts: EsaedroSortField[];
  loadedStarterFilters: boolean;
  columns: ResourceViewField[];

  lastSelectedObject: any;
  lastSelectedObjectRowIndex: number;
  lastSelectedObjectPageIndex: number;

  myObjUrl: string;
  myObjType: string;

  navigationFilters: EsaedroResourceFilter[];

  // customInsertFormComponentName: string,
  // showCustomInsertForm: boolean,

  isFirstLoad: boolean;
  forceRefetchData?: boolean;
}

export interface TabbedPageObjectEdit {
  id: number;
  objectToEdit: any;
  resource_ID: number | string;
  item_ID: number | string | undefined; // Poichè se è di tipo Resources, non ha un item_ID ma ha un Resource_ID
  lockedByMe: boolean;
}

export interface TabbedPageObjectDelete {
  id: number;
}

export interface TabbedPageAttachmentList {
  id: number;
}

export interface TabbedPageObjectListFilters {
  id: number;
  resourceName: string;
}
export interface TabbedPageMenuItemGridCustomAction {
  id: number;
  menuItemGridCustomAction: MenuItemGridCustomAction | undefined;
  selectedObjects: any[];
}

export interface ITabbedPage {
  name: string; // Dovrà essere univoco per ogni tab

  menuHistory: EsaedroHistoryItem[];
  selectedMenuHistoryItem: EsaedroHistoryItem;

  objectLists: TabbedPageObjectList[];
  countObjectLists: number;

  objectEdits: TabbedPageObjectEdit[];
  countObjectEdits: number;

  objectDeletes: TabbedPageObjectDelete[];
  countObjectDeletes: number;

  attachmentsLists: TabbedPageAttachmentList[];
  countAttachmentsLists: number;

  objectListFilters: TabbedPageObjectListFilters[];
  countObjectListFilters: number;

  menuItemGridCustomActions: TabbedPageMenuItemGridCustomAction[];
  countMenuItemGridCustomActions: number;

  order: string[]; // Serve per sapere l'ordine di cosa apro
  // isFirstLoad: boolean,
}

// Define a type for the slice state
interface TabbedPageState {
  tabbedPages: ITabbedPage[];
}

// Define the initial state using that type
const initialState: TabbedPageState = {
  tabbedPages: [],
};

export const tabbedPageSlice = createSlice({
  name: "tabbedPages",
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    addTabbedPage: (state: any, action: PayloadAction<ITabbedPage>) => {
      const tabbedPage: ITabbedPage | undefined = state.tabbedPages.find(
        (tabbedPage: ITabbedPage) => {
          return tabbedPage.name === action.payload.name;
        }
      );
      if (tabbedPage === undefined) {
        state.tabbedPages.push(action.payload);
      }
    },
    removeTabbedPage: (state: any, action: PayloadAction<string>) => {
      state.tabbedPages = state.tabbedPages.filter(
        (tabbedPage: ITabbedPage) => tabbedPage.name !== action.payload
      );
    },
    removeAllTabbedPages: (state: any) => {
      state.tabbedPages = [];
    },

    /* TABBED PAGE */
    setTabbedPageMenuHistory: (
      state: any,
      action: PayloadAction<{
        tabbedPageName: string;
        menuHistory: EsaedroHistoryItem[];
      }>
    ) => {
      const tabbedPage: ITabbedPage | undefined = state.tabbedPages.find(
        (tabbedPage: ITabbedPage) => {
          return tabbedPage.name === action.payload.tabbedPageName;
        }
      );
      if (tabbedPage !== undefined) {
        tabbedPage.menuHistory = action.payload.menuHistory;
      }
    },
    setTabbedPageMenuHistoryItem: (
      state: any,
      action: PayloadAction<{
        tabbedPageName: string;
        menuHistoryItem: EsaedroHistoryItem;
      }>
    ) => {
      const tabbedPage: ITabbedPage | undefined = state.tabbedPages.find(
        (tabbedPage: ITabbedPage) => {
          return tabbedPage.name === action.payload.tabbedPageName;
        }
      );
      if (tabbedPage !== undefined) {
        tabbedPage.selectedMenuHistoryItem = action.payload.menuHistoryItem;
      }
    },
    updateTabbedPageMenuHistoryAndItem: (
      state: any,
      action: PayloadAction<{
        tabbedPageName: string;
        menuHistory: EsaedroHistoryItem[];
        menuHistoryItem: EsaedroHistoryItem;
      }>
    ) => {
      const tabbedPage: ITabbedPage | undefined = state.tabbedPages.find(
        (tabbedPage: ITabbedPage) => {
          return tabbedPage.name === action.payload.tabbedPageName;
        }
      );
      if (tabbedPage !== undefined) {
        tabbedPage.menuHistory = action.payload.menuHistory;
        tabbedPage.selectedMenuHistoryItem = action.payload.menuHistoryItem;
      }
    },

    /* OBJECT LIST */
    addObjectList: (
      state: any,
      action: PayloadAction<{
        tabbedPageName: string;
        objectList: TabbedPageObjectList;
      }>
    ) => {
      const tabbedPage = state.tabbedPages.find(
        (tabbedPage: ITabbedPage) =>
          tabbedPage.name === action.payload.tabbedPageName
      );
      if (tabbedPage) {
        tabbedPage.objectLists.push(action.payload.objectList);
        tabbedPage.countObjectLists++;
        tabbedPage.order.push(
          ObjectNames.ObjectList +
            ObjectNames.Separator +
            action.payload.objectList.id.toString()
        );
      }
    },
    removeObjectList: (
      state: any,
      action: PayloadAction<{ tabbedPageName: string; objectListId: number }>
    ) => {
      const tabbedPage = state.tabbedPages.find(
        (tabbedPage: ITabbedPage) =>
          tabbedPage.name === action.payload.tabbedPageName
      );
      if (tabbedPage) {
        tabbedPage.objectLists = tabbedPage.objectLists.filter(
          (objectList: TabbedPageObjectList) =>
            objectList.id !== action.payload.objectListId
        );
        tabbedPage.countObjectLists--;
        tabbedPage.order = tabbedPage.order.filter(
          (order: string) =>
            order !==
            createObjectUniqueName(
              ObjectNames.ObjectList,
              action.payload.objectListId.toString()
            )
        );
      }
    },
    initializeObjectList: (
      state: any,
      action: PayloadAction<{
        tabbedPageName: string;
        objectListId: number;
        columns: ResourceViewField[];
        lastSelectedObject: any;
      }>
    ) => {
      const tabbedPage = state.tabbedPages.find(
        (tabbedPage: ITabbedPage) =>
          tabbedPage.name === action.payload.tabbedPageName
      );
      if (tabbedPage) {
        tabbedPage.objectLists[action.payload.objectListId].isFirstLoad = false;
        tabbedPage.objectLists[action.payload.objectListId].columns =
          action.payload.columns;
        tabbedPage.objectLists[action.payload.objectListId].lastSelectedObject =
          action.payload.lastSelectedObject;
        tabbedPage.objectLists[
          action.payload.objectListId
        ].lastSelectedObjectRowIndex = 1;
        tabbedPage.objectLists[
          action.payload.objectListId
        ].lastSelectedObjectPageIndex = 1;
      }
    },
    setLastSelectedObject: (
      state: any,
      action: PayloadAction<{
        tabbedPageName: string;
        lastSelectedObject: any;
        objectListId: number;
      }>
    ) => {
      const tabbedPage = state.tabbedPages.find(
        (tabbedPage: ITabbedPage) =>
          tabbedPage.name === action.payload.tabbedPageName
      );
      if (tabbedPage) {
        tabbedPage.objectLists[action.payload.objectListId].lastSelectedObject =
          action.payload.lastSelectedObject;
      }
    },
    setLastSelectedObjectRowIndex: (
      state: any,
      action: PayloadAction<{
        tabbedPageName: string;
        lastSelectedObjectRowIndex: number;
        objectListId: number;
      }>
    ) => {
      const tabbedPage = state.tabbedPages.find(
        (tabbedPage: ITabbedPage) =>
          tabbedPage.name === action.payload.tabbedPageName
      );
      if (tabbedPage) {
        tabbedPage.objectLists[
          action.payload.objectListId
        ].lastSelectedObjectRowIndex =
          action.payload.lastSelectedObjectRowIndex;
      }
    },
    setLastSelectedObjectPageIndex: (
      state: any,
      action: PayloadAction<{
        tabbedPageName: string;
        lastSelectedObjectPageIndex: number;
        objectListId: number;
      }>
    ) => {
      const tabbedPage = state.tabbedPages.find(
        (tabbedPage: ITabbedPage) =>
          tabbedPage.name === action.payload.tabbedPageName
      );
      if (tabbedPage) {
        tabbedPage.objectLists[
          action.payload.objectListId
        ].lastSelectedObjectPageIndex =
          action.payload.lastSelectedObjectPageIndex;
      }
    },
    updateLastSelectedObject: (
      state: any,
      action: PayloadAction<{
        tabbedPageName: string;
        lastSelectedObject: any;
        lastSelectedObjectRowIndex: number;
        lastSelectedObjectPageIndex: number;
        objectListId: number;
      }>
    ) => {
      const tabbedPage = state.tabbedPages.find(
        (tabbedPage: ITabbedPage) =>
          tabbedPage.name === action.payload.tabbedPageName
      );
      if (tabbedPage) {
        tabbedPage.objectLists[action.payload.objectListId].lastSelectedObject =
          action.payload.lastSelectedObject;
        tabbedPage.objectLists[
          action.payload.objectListId
        ].lastSelectedObjectRowIndex =
          action.payload.lastSelectedObjectRowIndex;
        tabbedPage.objectLists[
          action.payload.objectListId
        ].lastSelectedObjectPageIndex =
          action.payload.lastSelectedObjectPageIndex;
      }
    },
    setIsFirstLoad: (
      state: any,
      action: PayloadAction<{
        tabbedPageName: string;
        isFirstLoad: boolean;
        objectListId: number;
      }>
    ) => {
      const tabbedPage = state.tabbedPages.find(
        (tabbedPage: ITabbedPage) =>
          tabbedPage.name === action.payload.tabbedPageName
      );
      if (tabbedPage) {
        tabbedPage.objectLists[action.payload.objectListId].isFirstLoad =
          action.payload.isFirstLoad;
      }
    },
    setStarterResourceFilter: (
      state: any,
      action: PayloadAction<{
        tabbedPageName: string;
        objectListId: number;
        filters: EsaedroResourceFilter[];
      }>
    ) => {
      const tabbedPage = state.tabbedPages.find(
        (tabbedPage: ITabbedPage) =>
          tabbedPage.name === action.payload.tabbedPageName
      );
      if (tabbedPage) {
        tabbedPage.objectLists[
          action.payload.objectListId
        ].loadedStarterFilters = true;
        tabbedPage.objectLists[action.payload.objectListId].filters =
          action.payload.filters;
      }
    },

    setResourceFilters: (
      state: any,
      action: PayloadAction<{
        tabbedPageName: string;
        objectListId: number;
        filters: EsaedroResourceFilter[];
      }>
    ) => {
      const tabbedPage = state.tabbedPages.find(
        (tabbedPage: ITabbedPage) =>
          tabbedPage.name === action.payload.tabbedPageName
      );
      if (tabbedPage) {
        tabbedPage.objectLists[action.payload.objectListId].filters =
          action.payload.filters;
      }
    },
    addResourceFilter: (
      state: any,
      action: PayloadAction<{
        tabbedPageName: string;
        objectListId: number;
        filter: EsaedroResourceFilter;
      }>
    ) => {
      const tabbedPage = state.tabbedPages.find(
        (tabbedPage: ITabbedPage) =>
          tabbedPage.name === action.payload.tabbedPageName
      );
      if (tabbedPage) {
        var tmpFilters =
          tabbedPage.objectLists[action.payload.objectListId].filters;
        tmpFilters.push(action.payload.filter);
        tabbedPage.objectLists[action.payload.objectListId].forceRefetchData =
          true;
        tabbedPage.objectLists[action.payload.objectListId].filters =
          tmpFilters;
      }
    },
    removeResouceFilterFromArrayIndex: (
      state: any,
      action: PayloadAction<{
        tabbedPageName: string;
        objectListId: number;
        filterIndex: number;
      }>
    ) => {
      const tabbedPage = state.tabbedPages.find(
        (tabbedPage: ITabbedPage) =>
          tabbedPage.name === action.payload.tabbedPageName
      );
      if (tabbedPage) {
        var tmpFilters =
          tabbedPage.objectLists[action.payload.objectListId].filters;
        const filter =
          tabbedPage.objectLists[action.payload.objectListId].filters[
            action.payload.filterIndex
          ];
        if (filter) {
          tmpFilters.splice(action.payload.filterIndex, 1);
          tabbedPage.objectLists[action.payload.objectListId].forceRefetchData =
            true;
          tabbedPage.objectLists[action.payload.objectListId].filters =
            tmpFilters;
        }
      }
    },
    setResourceColumns: (
      state: any,
      action: PayloadAction<{
        tabbedPageName: string;
        objectListId: number;
        columns: ResourceViewField[];
      }>
    ) => {
      const tabbedPage = state.tabbedPages.find(
        (tabbedPage: ITabbedPage) =>
          tabbedPage.name === action.payload.tabbedPageName
      );
      if (tabbedPage) {
        tabbedPage.objectLists[action.payload.objectListId].columns =
          action.payload.columns;
      }
    },
    setCustomInsertFormComponentName: (
      state: any,
      action: PayloadAction<{
        tabbedPageName: string;
        objectListId: number;
        customInsertFormComponentName: string;
      }>
    ) => {
      const tabbedPage = state.tabbedPages.find(
        (tabbedPage: ITabbedPage) =>
          tabbedPage.name === action.payload.tabbedPageName
      );
      if (tabbedPage) {
        tabbedPage.objectLists[
          action.payload.objectListId
        ].customInsertFormComponentName =
          action.payload.customInsertFormComponentName;
      }
    },
    setShowCustomInsertForm: (
      state: any,
      action: PayloadAction<{
        tabbedPageName: string;
        objectListId: number;
        showCustomInsertForm: boolean;
      }>
    ) => {
      const tabbedPage = state.tabbedPages.find(
        (tabbedPage: ITabbedPage) =>
          tabbedPage.name === action.payload.tabbedPageName
      );
      if (tabbedPage) {
        tabbedPage.objectLists[
          action.payload.objectListId
        ].showCustomInsertForm = action.payload.showCustomInsertForm;
      }
    },
    setForceRefetchData: (
      state: any,
      action: PayloadAction<{
        tabbedPageName: string;
        objectListId: number;
        forceRefetchData: boolean;
      }>
    ) => {
      const tabbedPage = state.tabbedPages.find(
        (tabbedPage: ITabbedPage) =>
          tabbedPage.name === action.payload.tabbedPageName
      );
      if (tabbedPage) {
        tabbedPage.objectLists[action.payload.objectListId].forceRefetchData =
          action.payload.forceRefetchData;
      }
    },

    /* OBJECT EDIT */
    addObjectEdit: (
      state: any,
      action: PayloadAction<{
        tabbedPageName: string;
        objectEdit: TabbedPageObjectEdit;
      }>
    ) => {
      const tabbedPage = state.tabbedPages.find(
        (tabbedPage: ITabbedPage) =>
          tabbedPage.name === action.payload.tabbedPageName
      );
      if (tabbedPage) {
        tabbedPage.objectEdits.push(action.payload.objectEdit);
        tabbedPage.countObjectEdits++;
        tabbedPage.order.push(
          ObjectNames.ObjectEdit +
            ObjectNames.Separator +
            action.payload.objectEdit.id.toString()
        );
      }
    },
    removeObjectEdit: (
      state: any,
      action: PayloadAction<{ tabbedPageName: string; objectEditId: number }>
    ) => {
      const tabbedPage = state.tabbedPages.find(
        (tabbedPage: ITabbedPage) =>
          tabbedPage.name === action.payload.tabbedPageName
      );
      if (tabbedPage) {
        tabbedPage.objectEdits = tabbedPage.objectEdits.filter(
          (objectEdit: TabbedPageObjectEdit) =>
            objectEdit.id !== action.payload.objectEditId
        );
        tabbedPage.countObjectEdits--;
        tabbedPage.order = tabbedPage.order.filter(
          (order: string) =>
            order !==
            createObjectUniqueName(
              ObjectNames.ObjectEdit,
              action.payload.objectEditId.toString()
            )
        );
      }
    },
    setObjectToEdit: (
      state: any,
      action: PayloadAction<{
        tabbedPageName: string;
        objectEditId: number;
        object: any;
      }>
    ) => {
      const tabbedPage = state.tabbedPages.find(
        (tabbedPage: ITabbedPage) =>
          tabbedPage.name === action.payload.tabbedPageName
      );
      if (tabbedPage) {
        const objectEdit = tabbedPage.objectEdits.find(
          (objectEdit: TabbedPageObjectEdit) =>
            objectEdit.id === action.payload.objectEditId
        );
        if (objectEdit) {
          objectEdit.objectToEdit = action.payload.object;
        }
      }
    },
    setLockedByMe: (
      state: any,
      action: PayloadAction<{
        tabbedPageName: string;
        objectEditId: number;
        lockedByMe: boolean;
      }>
    ) => {
      const tabbedPage = state.tabbedPages.find(
        (tabbedPage: ITabbedPage) =>
          tabbedPage.name === action.payload.tabbedPageName
      );
      if (tabbedPage) {
        const objectEdit = tabbedPage.objectEdits.find(
          (objectEdit: TabbedPageObjectEdit) =>
            objectEdit.id === action.payload.objectEditId
        );
        if (objectEdit) {
          objectEdit.lockedByMe = action.payload.lockedByMe;
        }
      }
    },

    /* OBJECT DELETE */
    addObjectDelete: (
      state: any,
      action: PayloadAction<{
        tabbedPageName: string;
        objectDelete: TabbedPageObjectDelete;
      }>
    ) => {
      const tabbedPage = state.tabbedPages.find(
        (tabbedPage: ITabbedPage) =>
          tabbedPage.name === action.payload.tabbedPageName
      );
      if (tabbedPage) {
        tabbedPage.objectDeletes.push(action.payload.objectDelete);
        tabbedPage.countObjectDeletes++;
        tabbedPage.order.push(
          ObjectNames.ObjectDelete +
            ObjectNames.Separator +
            action.payload.objectDelete.id.toString()
        );
      }
    },
    removeObjectDelete: (
      state: any,
      action: PayloadAction<{ tabbedPageName: string; objectDeleteId: number }>
    ) => {
      const tabbedPage = state.tabbedPages.find(
        (tabbedPage: ITabbedPage) =>
          tabbedPage.name === action.payload.tabbedPageName
      );
      if (tabbedPage) {
        tabbedPage.objectDeletes = tabbedPage.objectDeletes.filter(
          (objectDelete: TabbedPageObjectDelete) =>
            objectDelete.id !== action.payload.objectDeleteId
        );
        tabbedPage.countObjectDeletes--;
        tabbedPage.order = tabbedPage.order.filter(
          (order: string) =>
            order !==
            createObjectUniqueName(
              ObjectNames.ObjectDelete,
              action.payload.objectDeleteId.toString()
            )
        );
      }
    },

    /* ATTACHMENT LIST */
    addAttachmentsList: (
      state: any,
      action: PayloadAction<{
        tabbedPageName: string;
        attachmentsList: TabbedPageAttachmentList;
      }>
    ) => {
      const tabbedPage = state.tabbedPages.find(
        (tabbedPage: ITabbedPage) =>
          tabbedPage.name === action.payload.tabbedPageName
      );
      if (tabbedPage) {
        tabbedPage.attachmentsLists.push(action.payload.attachmentsList);
        tabbedPage.countAttachmentsLists++;
        tabbedPage.order.push(
          ObjectNames.AttachmentsList +
            ObjectNames.Separator +
            action.payload.attachmentsList.id.toString()
        );
      }
    },
    removeAttachmentsList: (
      state: any,
      action: PayloadAction<{
        tabbedPageName: string;
        attachmentsListId: number;
      }>
    ) => {
      const tabbedPage = state.tabbedPages.find(
        (tabbedPage: ITabbedPage) =>
          tabbedPage.name === action.payload.tabbedPageName
      );
      if (tabbedPage) {
        tabbedPage.attachmentsLists = tabbedPage.attachmentsLists.filter(
          (objectAttachmentList: TabbedPageAttachmentList) =>
            objectAttachmentList.id !== action.payload.attachmentsListId
        );
        tabbedPage.countAttachmentsLists--;
        tabbedPage.order = tabbedPage.order.filter(
          (order: string) =>
            order !==
            createObjectUniqueName(
              ObjectNames.AttachmentsList,
              action.payload.attachmentsListId.toString()
            )
        );
      }
    },

    /* OBJECTLISTFILTERS */
    addObjectListFilters: (
      state: any,
      action: PayloadAction<{
        tabbedPageName: string;
        objectListFilters: TabbedPageObjectListFilters;
      }>
    ) => {
      const tabbedPage = state.tabbedPages.find(
        (tabbedPage: ITabbedPage) =>
          tabbedPage.name === action.payload.tabbedPageName
      );
      if (tabbedPage) {
        tabbedPage.objectListFilters.push(action.payload.objectListFilters);
        tabbedPage.countObjectListFilters++;
        tabbedPage.order.push(
          ObjectNames.ObjectListFilters +
            ObjectNames.Separator +
            action.payload.objectListFilters.id.toString()
        );
      }
    },

    removeObjectListFilters: (
      state: any,
      action: PayloadAction<{
        tabbedPageName: string;
        objectListFiltersId: number;
      }>
    ) => {
      const tabbedPage = state.tabbedPages.find(
        (tabbedPage: ITabbedPage) =>
          tabbedPage.name === action.payload.tabbedPageName
      );
      if (tabbedPage) {
        tabbedPage.objectListFilters = tabbedPage.objectListFilters.filter(
          (objectListFilters: TabbedPageObjectListFilters) =>
            objectListFilters.id !== action.payload.objectListFiltersId
        );
        tabbedPage.countObjectListFilters--;
        tabbedPage.order = tabbedPage.order.filter(
          (order: string) =>
            order !==
            createObjectUniqueName(
              ObjectNames.ObjectListFilters,
              action.payload.objectListFiltersId.toString()
            )
        );
      }
    },

    addMenuItemGridCustomAction: (
      state: any,

      action: PayloadAction<{
        tabbedPageName: string;

        menuItemGridCustomAction: TabbedPageMenuItemGridCustomAction;
      }>
    ) => {
      const tabbedPage = state.tabbedPages.find(
        (tabbedPage: ITabbedPage) =>
          tabbedPage.name === action.payload.tabbedPageName
      );

      if (tabbedPage) {
        tabbedPage.menuItemGridCustomActions.push(
          action.payload.menuItemGridCustomAction
        );

        tabbedPage.countMenuItemGridCustomActions++;

        tabbedPage.order.push(
          ObjectNames.MenuItemGridCustomAction +
            ObjectNames.Separator +
            action.payload.menuItemGridCustomAction.id.toString()
        );
      }
    },

    removeMenuItemGridCustomAction: (
      state: any,
      action: PayloadAction<{
        tabbedPageName: string;
        menuItemGridCustomActionId: number;
      }>
    ) => {
      const tabbedPage = state.tabbedPages.find(
        (tabbedPage: ITabbedPage) =>
          tabbedPage.name === action.payload.tabbedPageName
      );
      if (tabbedPage) {
        tabbedPage.menuItemGridCustomActions =
          tabbedPage.menuItemGridCustomActions.filter(
            (
              objectMenuItemGridCustomAction: TabbedPageMenuItemGridCustomAction
            ) =>
              objectMenuItemGridCustomAction.id !==
              action.payload.menuItemGridCustomActionId
          );
        tabbedPage.countMenuItemGridCustomActions--;
        tabbedPage.order = tabbedPage.order.filter(
          (order: string) =>
            order !==
            createObjectUniqueName(
              ObjectNames.MenuItemGridCustomAction,
              action.payload.menuItemGridCustomActionId.toString()
            )
        );
      }
    },
  },
});

export const {
  addTabbedPage,
  removeTabbedPage,
  removeAllTabbedPages,

  setTabbedPageMenuHistory,
  setTabbedPageMenuHistoryItem,
  updateTabbedPageMenuHistoryAndItem,

  initializeObjectList,
  setLastSelectedObject,
  setStarterResourceFilter,
  setResourceFilters,
  addResourceFilter,
  removeResouceFilterFromArrayIndex,
  setResourceColumns,
  setLastSelectedObjectRowIndex,
  setLastSelectedObjectPageIndex,
  updateLastSelectedObject,
  setIsFirstLoad,
  addObjectList,
  removeObjectList,
  setCustomInsertFormComponentName,
  setShowCustomInsertForm,
  setForceRefetchData,

  addObjectEdit,
  removeObjectEdit,
  setObjectToEdit,
  setLockedByMe,

  addObjectDelete,
  removeObjectDelete,

  addAttachmentsList,
  removeAttachmentsList,

  addObjectListFilters,
  removeObjectListFilters,

  addMenuItemGridCustomAction,
  removeMenuItemGridCustomAction,
} = tabbedPageSlice.actions;

// Other code such as selectors can use the imported `RootState` type
// export const selectIsLoggedIn = (state: RootState) => state.user.isLoggedIn

export default tabbedPageSlice.reducer;
